import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSPDRosterDuesDetail,
  getSPDRosterDuesSummary,
  approveRosterDuesForm,
  rejectRosterDuesForm,
} from '../../../endpoints/rosterDues';
import AlertItem from '../../../Components/AlertItem';
import {
  RosterDuesDetail,
  SPDRosterFormSummary,
  ApprovalRequest,
  RejectionRequest,
} from '../../../types/rosterDues';
import dayjs from 'dayjs';
import { formTypeToStatusArrayMap } from '../../../utils/variables';

// Format currency helper
const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const RosterDuesSPDSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('user_id');
  const userFullName =
    `${localStorage.getItem('first_name') || ''} ${localStorage.getItem('last_name') || ''}`.trim();

  const [detail, setDetail] = useState<RosterDuesDetail | null>(null);
  const [summary, setSummary] = useState<SPDRosterFormSummary | null>(null);
  const [notesError, setNotesError] = useState(false);
  const [notesErrorDescription, setNotesErrorDescription] = useState('');
  const [signatureError, setSignatureError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [modalNotes, setModalNotes] = useState('');
  const [modalSignature, setModalSignature] = useState('');
  const [modalUnderstandChecked, setModalUnderstandChecked] = useState(false);

  const {
    data: detailData,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useApiGet(
    ['spd-roster-dues-detail', id],
    () => getSPDRosterDuesDetail(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useApiGet(
    ['spd-roster-dues-summary', id],
    () => getSPDRosterDuesSummary(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    // Clear state when id changes
    setDetail(null);
    setSummary(null);
    setShowSuccess(false);
    setSuccessMessage('');
    setNotesError(false);
    setNotesErrorDescription('');
    setSignatureError(false);
    setCheckboxError(false);
    setRejectModalOpen(false);
    setApproveModalOpen(false);
    setModalNotes('');
    setModalSignature('');
    setModalUnderstandChecked(false);
  }, [id]);

  // Refetch data on mount to ensure fresh data
  useEffect(() => {
    if (id) {
      refetchDetail();
      refetchSummary();
    }
  }, [id, refetchDetail, refetchSummary]);

  useEffect(() => {
    if (detailData) {
      setDetail(detailData);
    }
  }, [detailData]);

  useEffect(() => {
    if (summaryData) {
      setSummary(summaryData);
    }
  }, [summaryData]);

  const canEdit = detail !== null && detail.assigned_to_id === currentUserId;

  // Calculate totals
  const totalRemittance =
    (summary?.dues_total || 0) +
    (summary?.cottey_college_total || 0) +
    (summary?.initiation_total || 0) +
    (summary?.reinstated_total || 0) +
    (summary?.tech_total || 0) +
    (summary?.liability_insurance || 0) +
    (summary?.insurance_premium || 0) +
    (summary?.bond_insurance || 0) +
    (summary?.qb_usage_charge || 0);

  const { mutate: approveForm, isPending: isApproving } = useApiSend(
    (params: { formId: string; data: ApprovalRequest }) => {
      return approveRosterDuesForm(params.formId, params.data);
    },
    () => {
      setApproveModalOpen(false);
      setShowSuccess(true);
      setSuccessMessage('Form approved successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate('/annual-reports');
      }, 2500);
    },
    (error) => {
      console.error('Failed to approve form:', error);
      setNotesError(true);
      setNotesErrorDescription(
        'Error approving form. If the problem persists, please contact support.'
      );
    },
    [
      ['spd-roster-dues-detail', id],
      ['spd-roster-dues-summary', id],
      ['roster-dues-list'],
    ]
  );

  const { mutate: rejectForm, isPending: isRejecting } = useApiSend(
    (params: { formId: string; data: RejectionRequest }) => {
      return rejectRosterDuesForm(params.formId, params.data);
    },
    () => {
      setRejectModalOpen(false);
      setShowSuccess(true);
      setSuccessMessage('Form rejected successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate('/annual-reports');
      }, 2500);
    },
    (error) => {
      console.error('Failed to reject form:', error);
      setNotesError(true);
      setNotesErrorDescription(
        'Error rejecting form. If the problem persists, please contact support.'
      );
    },
    [
      ['spd-roster-dues-detail', id],
      ['spd-roster-dues-summary', id],
      ['roster-dues-list'],
    ]
  );

  // Determine if rejection button should be hidden
  const shouldHideRejectionButton = (): boolean => {
    if (!detail?.status_id) return false;

    const statusArray = formTypeToStatusArrayMap['roster_spd'];

    if (!statusArray || statusArray.length === 0) return false;

    const firstStatusId = statusArray[0].id;
    return detail.status_id === firstStatusId;
  };

  const handleRejectClick = () => {
    setModalNotes('');
    setNotesError(false);
    setNotesErrorDescription('');
    setRejectModalOpen(true);
  };

  const handleApproveClick = () => {
    setModalSignature('');
    setModalUnderstandChecked(false);
    setSignatureError(false);
    setCheckboxError(false);
    setApproveModalOpen(true);
  };

  const handleApprove = () => {
    if (!id || !summary) return;

    let valid = true;

    if (!modalUnderstandChecked) {
      setCheckboxError(true);
      valid = false;
    }

    if (modalSignature !== userFullName) {
      setSignatureError(true);
      valid = false;
    }

    if (!valid) return;

    approveForm({
      formId: id,
      data: {
        signature: modalSignature,
        understand_checked: modalUnderstandChecked,
        remit_total: totalRemittance,
        active_members: summary.active_members,
        reinstated_members: summary.reinstated,
        initiated_members: summary.initiated,
        tech_total: summary.tech_total || 0,
        others_total: 0, // SPD forms don't have other_fees
      },
    });
  };

  const handleReject = () => {
    if (!id) return;

    let valid = true;

    if (modalNotes.length === 0) {
      setNotesError(true);
      setNotesErrorDescription('Notes are required for rejection');
      valid = false;
    } else if (modalNotes.length > 4000) {
      setNotesError(true);
      setNotesErrorDescription('Notes must be less than 4000 characters');
      valid = false;
    }

    if (!valid) return;

    rejectForm({
      formId: id,
      data: {
        note: modalNotes,
      },
    });
  };

  if (detailLoading || summaryLoading) {
    return (
      <Grid container padding={4} justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (!detail || !summary) {
    return (
      <Grid container padding={4}>
        <AlertItem
          severity="error"
          description="Failed to load form data."
          onClose={() => {}}
          keepOpen={true}
        />
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      {showSuccess && (
        <Grid item xs={12} padding={2}>
          <Alert severity="success">{successMessage}</Alert>
        </Grid>
      )}

      <Grid item xs={12} padding={1}>
        <Typography variant="h4" color={'primary'}>
          {detail.spd_chapter_name || 'SPD Roster & Dues Summary'}
        </Typography>
        <Typography variant="body2" color={'text.secondary'} sx={{ mt: 1 }}>
          Status: {detail.status_name}
        </Typography>
        {detail.assigned_to && (
          <Typography variant="body2" color={'text.secondary'}>
            Assigned to: {detail.assigned_to}
          </Typography>
        )}
      </Grid>

      {!canEdit && (
        <Grid item xs={12} padding={2}>
          <Alert severity="info">
            You are viewing this form in read-only mode. Only the assigned user
            can make changes.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} paddingBottom={2}>
        <Paper sx={{ p: 3 }}>
          {/* Summary Information */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="medium">
                Number of Active Chapters Reporting March 1, 2026:{' '}
                {summary.active_chapters}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="medium">
                Number of Active Members March 1, 2026: {summary.active_members}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section I: Dues */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="bold">
                I. Amount of dues payable to International Chapter per active
                member:
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">
                International Chapter dues of $
                {(summary.dues_amount + summary.cottey_college_amount).toFixed(
                  2
                )}{' '}
                (includes ${summary.cottey_college_amount.toFixed(2)} for Cottey
                College):
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(
                  summary.dues_total + summary.cottey_college_total
                )}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section II: Initiation and Reinstatement Fees */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="bold">
                II. Initiation and Reinstatement Fees:
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">
                ${summary.initiation_fee.toFixed(2)} for each of{' '}
                {summary.initiated} members initiated 03/01/2024 to 02/28/2025:
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(summary.initiation_total)}
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">
                ${summary.reinstated_fee.toFixed(2)} for each of{' '}
                {summary.reinstated} members reinstated 03/01/2024 to
                02/28/2025:
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(summary.reinstated_total)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section III: Supply/Resource/Technology Payment */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="bold">
                III. Supply/Resource/Technology Payment:
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1">Technology Fee:</Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1">
                {formatCurrency(summary.tech_total)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section IV: Liability Insurance */}
          {summary.liability_insurance !== undefined &&
            summary.liability_insurance !== null && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold">
                      IV. Liability Insurance, $1 per active chapter as of
                      3/1/2026:
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body1">
                      {summary.active_chapters} active local chapters + 1{' '}
                      {detail.spd_chapter_name} chapter
                    </Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography variant="body1">
                      {formatCurrency(summary.liability_insurance)}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
              </>
            )}

          {/* Section V: Directors & Officers Insurance Premium */}
          {summary.insurance_premium !== undefined &&
            summary.insurance_premium !== null && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold">
                      V. Directors & Officers Insurance Premium:
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body1">
                      Directors & Officers Insurance Premium:
                    </Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography variant="body1">
                      {formatCurrency(summary.insurance_premium)}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
              </>
            )}

          {/* Section VI: Fidelity Bond Insurance */}
          {summary.bond_insurance !== undefined &&
            summary.bond_insurance !== null && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold">
                      VI. Fidelity Bond Insurance:
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body1">
                      Fidelity Bond Insurance
                      {summary.officers_covered && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                          sx={{ ml: 1 }}
                        >
                          ({summary.officers_covered})
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography variant="body1">
                      {formatCurrency(summary.bond_insurance)}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
              </>
            )}

          {/* Section VII: QuickBooks Usage Fee */}
          {summary.qb_usage_charge !== undefined &&
            summary.qb_usage_charge !== null && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold">
                      VII. QuickBooks Usage Fee:
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body1">
                      QuickBooks Usage Fee:
                    </Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography variant="body1">
                      {formatCurrency(summary.qb_usage_charge)}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
              </>
            )}

          {/* Section VIII: Total Remittance */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={9}>
              <Typography variant="body1" fontWeight="bold">
                VIII. Total Remittance:
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(totalRemittance)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Previous Notes - Always visible */}
      {summary.notes && summary.notes.length > 0 && (
        <Grid item xs={12} paddingTop={2}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Previous Notes:
          </Typography>
          {summary.notes.map((note) => (
            <Grid
              container
              key={note.id}
              sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}
            >
              <Grid item xs={12} sm={12} md={5} lg={4}>
                <Typography variant="body2" color="text.secondary">
                  {note.name} on{' '}
                  {dayjs(note.created_on).format('MM/DD/YYYY hh:mm A')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={7} lg={8}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {note.note}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Buttons - Only visible to assigned user */}
      {canEdit && (
        <Grid item xs={12} paddingTop={4} paddingBottom={2}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/annual-reports/roster-dues/spd')}
              disabled={isApproving || isRejecting}
            >
              Back to List
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/annual-reports/roster-dues/${id}/chapters`)
              }
              disabled={isApproving || isRejecting}
            >
              Back to Chapters
            </Button>
            {!shouldHideRejectionButton() && (
              <Button
                variant="contained"
                color="error"
                onClick={handleRejectClick}
                disabled={!canEdit || isApproving || isRejecting}
              >
                SUBMIT AS REJECTION
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleApproveClick}
              disabled={!canEdit || isApproving || isRejecting}
            >
              SUBMIT AS APPROVED
            </Button>
          </Box>
        </Grid>
      )}

      {/* Back buttons for non-assigned users */}
      {!canEdit && (
        <Grid item xs={12} paddingTop={4} paddingBottom={2}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/annual-reports/roster-dues/spd')}
            >
              Back to List
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/annual-reports/roster-dues/${id}/chapters`)
              }
            >
              Back to Chapters
            </Button>
          </Box>
        </Grid>
      )}

      {/* Rejection Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !isRejecting && setRejectModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Submit as Rejection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Please provide an explanation for rejecting this form. This note is
            required.
          </Typography>
          <TextField
            id="rejection-notes"
            label="Explanation / Notes *"
            multiline
            rows={6}
            fullWidth
            value={modalNotes}
            onChange={(e) => {
              const value = e.target.value;
              setModalNotes(value);
              if (value.length > 4000) {
                setNotesError(true);
                setNotesErrorDescription(
                  'Notes must be less than 4000 characters'
                );
              } else if (value.length > 0) {
                setNotesError(false);
                setNotesErrorDescription('');
              }
            }}
            error={notesError}
            helperText={
              notesErrorDescription || 'Notes are required for rejection'
            }
            disabled={isRejecting}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={isRejecting}
            startIcon={
              isRejecting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isRejecting ? 'SUBMITTING...' : 'Submit Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={() => !isApproving && setApproveModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit as Approved</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={modalUnderstandChecked}
                onChange={(e) => {
                  setModalUnderstandChecked(e.target.checked);
                  setCheckboxError(false);
                }}
                disabled={isApproving}
                sx={checkboxError ? { color: 'error.main' } : {}}
              />
            }
            label="I hereby attest that the information above is accurate and balanced.*"
            sx={{ mt: 2, mb: 2 }}
          />
          {checkboxError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mb: 2 }}
            >
              This checkbox must be checked to submit.
            </Typography>
          )}

          <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
            Electronic Signature:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please type your name in the box. The name you enter must match the
            name shown below the box, including punctuation and spacing.
          </Typography>
          <TextField
            id="approval-signature"
            label="Name*"
            fullWidth
            value={modalSignature}
            onChange={(e) => {
              setModalSignature(e.target.value);
              if (e.target.value === userFullName) {
                setSignatureError(false);
              }
            }}
            error={signatureError}
            helperText={
              signatureError ? 'Name must match the name shown below' : ''
            }
            disabled={isApproving}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {userFullName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveModalOpen(false)}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="primary"
            disabled={isApproving}
            startIcon={
              isApproving ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isApproving ? 'SUBMITTING...' : 'Submit Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RosterDuesSPDSummary;
